import {
  WESTERN_PLANETS,
  type AnglesResult,
  type AspectHit,
  type AyanamsaId,
  type HouseSystem,
  type HousesResult,
  type Planet,
  type PlanetPosition,
  type SignPosition,
} from '@astrally/shared';
import type { EphemerisAdapter } from '../ephemeris/adapter.js';
import { getAyanamsa } from '../ayanamsa/lahiri.js';
import { angleDiff, norm360 } from '../math/angles.js';
import { ayanamsaValue, computePositions } from '../core/positions.js';
import { computeAngles, houseOf, housesFor } from '../core/houses.js';
import { signPosition } from '../core/zodiac.js';
import { computeAspects, type ChartPoint } from './aspects.js';

/**
 * Returns: the instant a body comes back to the exact longitude it held in the
 * natal chart, and the chart cast for that instant.
 *
 * Two things make this module worth its own file:
 *
 * 1. The instant is SOLVED, never estimated. Mean motions below are used only
 *    to size the search window; every instant reported here is a bracketed
 *    root of `bodyLongitude(t) − natalLongitude = 0` evaluated against the
 *    ephemeris adapter and bisected to under a second.
 * 2. A return chart is LOCATION-DEPENDENT. The moment of the return is the
 *    same everywhere on Earth, but the angles and houses are not — which is
 *    the entire point of a solar return: where you are on your birthday sets
 *    the houses the year's chart is read through.
 *
 * Interpretation note for anything narrating this output: a return marks a
 * cycle starting over. It is a lens on a period, never a forecast of events.
 */

const MS_PER_DAY = 86_400_000;

/** Days in a mean tropical year. */
export const TROPICAL_YEAR_DAYS = 365.242_19;

export type ReturnZodiac = 'tropical' | 'sidereal';

/**
 * Mean geocentric daily motion in ecliptic longitude, degrees/day.
 * Mercury and Venus orbit inside Earth's orbit, so seen from Earth they keep
 * pace with the Sun: their mean geocentric motion is the Sun's, and they come
 * back to a natal degree about once a year, not once per orbit.
 */
export const MEAN_DAILY_MOTION: Record<Planet, number> = {
  sun: 0.985_609,
  moon: 13.176_358,
  mercury: 0.985_609,
  venus: 0.985_609,
  mars: 0.524_033,
  jupiter: 0.083_091,
  saturn: 0.033_460,
  uranus: 0.011_725,
  neptune: 0.005_995,
  pluto: 0.003_964,
  rahu: 0.052_954,
  ketu: 0.052_954,
};

/** Mean interval between successive returns to the same longitude, days. */
export const MEAN_RETURN_DAYS: Record<Planet, number> = Object.fromEntries(
  Object.entries(MEAN_DAILY_MOTION).map(([planet, motion]) => [planet, 360 / motion]),
) as Record<Planet, number>;

/**
 * Widest retrograde loop each body makes in ecliptic longitude, degrees.
 * A planet that retrogrades can cross the same degree three times in a few
 * months; this bounds that zone so the search never mistakes a retrograde
 * re-crossing for the next return.
 */
const RETRO_LOOP_DEG: Record<Planet, number> = {
  sun: 0,
  moon: 0,
  mercury: 14,
  venus: 17,
  mars: 20,
  jupiter: 11,
  saturn: 8,
  uranus: 5,
  neptune: 3.5,
  pluto: 3.5,
  rahu: 0,
  ketu: 0,
};

/**
 * Longest retrograde season each body has, days. The three passes of a
 * retrograde return spread over roughly twice this, so it — not the loop
 * width — is what bounds the window the passes are collected from.
 */
const RETRO_SEASON_DAYS: Record<Planet, number> = {
  sun: 0,
  moon: 0,
  mercury: 24,
  venus: 43,
  mars: 82,
  jupiter: 123,
  saturn: 140,
  uranus: 155,
  neptune: 158,
  pluto: 162,
  rahu: 0,
  ketu: 0,
};

/**
 * Sampling step for bracketing a crossing, days. Small enough that no body
 * moves more than a few degrees between samples and no retrograde loop can be
 * stepped over.
 */
const SCAN_STEP_DAYS: Record<Planet, number> = {
  sun: 4,
  moon: 0.25,
  mercury: 0.5,
  venus: 1,
  mars: 2,
  jupiter: 5,
  saturn: 5,
  uranus: 5,
  neptune: 5,
  pluto: 5,
  rahu: 5,
  ketu: 5,
};

/** Plain-language cycle notes, for narration. Meaning, never prediction. */
export const RETURN_CYCLE_NOTES: Partial<Record<Planet, string>> = {
  sun: 'The Sun is back on your natal degree once a year, within a day of your birthday. The chart for that instant is read as a lens on the year ahead.',
  moon: 'The Moon returns to its natal degree about every 27.3 days — a monthly emotional reset point.',
  venus: 'Seen from Earth, Venus comes back to a natal degree roughly once a year, and those returns land on almost the same date and degree every 8 years — the Venus pentagram.',
  mars: 'Mars returns about every 2 years (1.9), often read as a fresh cycle of drive and initiative.',
  jupiter: 'Jupiter returns about every 12 years — near ages 12, 24, 36, 48. Read as a chapter change in outlook and growth.',
  saturn: 'Saturn returns about every 29.5 years — the famous "Saturn return" near ages 29-30, then 58-60. It is read as a lens on responsibility, structure and what you are actually building, not as a forecast that something will happen.',
  uranus: 'Uranus takes about 84 years to return; its opposition near age 42 is the point most people meet.',
  rahu: 'The lunar nodes return about every 18.6 years — ages 18-19, 37, 55-56.',
};

// ---------------------------------------------------------------------------
// Crossing search
// ---------------------------------------------------------------------------

export interface CrossingSearchOptions {
  /** Which zodiac the target longitude is expressed in. Default tropical. */
  zodiac?: ReturnZodiac;
  /** Only used when `zodiac` is sidereal. Default lahiri. */
  ayanamsaId?: AyanamsaId;
  /** Sampling step in days. Defaults per body — override only for tuning. */
  stepDays?: number;
}

/** One exact moment the body sits on the target longitude. */
export interface ReturnPass {
  utc: Date;
  utcISO: string;
  /** Body longitude at that instant, in the zodiac the search used. */
  lon: number;
  /** |lon − target| in degrees. Solved to well under an arcsecond. */
  residualDeg: number;
  retrograde: boolean;
}

function longitudeAt(
  planet: Planet,
  utc: Date,
  adapter: EphemerisAdapter,
  zodiac: ReturnZodiac,
  ayanamsaId: AyanamsaId,
): { lon: number; speed: number } {
  const state = adapter.position(planet, utc);
  if (zodiac === 'tropical') return { lon: norm360(state.lon), speed: state.speed };
  const ayan = getAyanamsa(ayanamsaId).valueDeg(adapter.julianCenturiesTT(utc));
  return { lon: norm360(state.lon - ayan), speed: state.speed };
}

/** Signed distance from the target in (-180, 180]. Zero at the crossing. */
function offsetAt(
  planet: Planet,
  target: number,
  ms: number,
  adapter: EphemerisAdapter,
  zodiac: ReturnZodiac,
  ayanamsaId: AyanamsaId,
): number {
  return angleDiff(longitudeAt(planet, new Date(ms), adapter, zodiac, ayanamsaId).lon, target);
}

function makePass(
  planet: Planet,
  target: number,
  ms: number,
  adapter: EphemerisAdapter,
  zodiac: ReturnZodiac,
  ayanamsaId: AyanamsaId,
): ReturnPass {
  const utc = new Date(ms);
  const { lon, speed } = longitudeAt(planet, utc, adapter, zodiac, ayanamsaId);
  return {
    utc,
    utcISO: utc.toISOString(),
    lon,
    residualDeg: Math.abs(angleDiff(lon, target)),
    // Rahu/Ketu regress by convention, matching computePositions().
    retrograde: planet === 'rahu' || planet === 'ketu' ? true : speed < 0,
  };
}

/**
 * Bisect a bracketed crossing down to the millisecond — three orders of
 * magnitude tighter than the "under a second" a return chart needs.
 */
function bisectCrossing(
  planet: Planet,
  target: number,
  loMs: number,
  hiMs: number,
  loOffset: number,
  adapter: EphemerisAdapter,
  zodiac: ReturnZodiac,
  ayanamsaId: AyanamsaId,
): ReturnPass {
  let lo = loMs;
  let hi = hiMs;
  let loSign = Math.sign(loOffset);
  for (let i = 0; i < 80 && hi - lo > 1; i++) {
    const mid = Math.floor((lo + hi) / 2);
    if (mid === lo || mid === hi) break;
    const g = offsetAt(planet, target, mid, adapter, zodiac, ayanamsaId);
    if (g === 0) {
      lo = mid;
      hi = mid;
      break;
    }
    if (Math.sign(g) === loSign) {
      lo = mid;
      loSign = Math.sign(g);
    } else {
      hi = mid;
    }
  }
  return makePass(planet, target, Math.round((lo + hi) / 2), adapter, zodiac, ayanamsaId);
}

/** A sign flip only counts as a crossing when it happens near the target, not at the ±180° seam. */
function isCrossing(a: number, b: number): boolean {
  return a !== b && Math.sign(a) !== Math.sign(b) && Math.abs(a) < 90 && Math.abs(b) < 90;
}

/**
 * Every instant in [fromUtc, toUtc] at which `planet` sits exactly on
 * `targetLon`. A retrograding planet can produce three in a single season.
 */
export function findLongitudeCrossings(
  planet: Planet,
  targetLon: number,
  fromUtc: Date,
  toUtc: Date,
  adapter: EphemerisAdapter,
  opts: CrossingSearchOptions = {},
): ReturnPass[] {
  const zodiac = opts.zodiac ?? 'tropical';
  const ayanamsaId = opts.ayanamsaId ?? 'lahiri';
  const stepMs = (opts.stepDays ?? SCAN_STEP_DAYS[planet]) * MS_PER_DAY;
  const target = norm360(targetLon);
  const endMs = toUtc.getTime();

  const hits: ReturnPass[] = [];
  let tMs = fromUtc.getTime();
  let g = offsetAt(planet, target, tMs, adapter, zodiac, ayanamsaId);
  while (tMs < endMs) {
    const nextMs = Math.min(tMs + stepMs, endMs);
    const nextG = offsetAt(planet, target, nextMs, adapter, zodiac, ayanamsaId);
    if (isCrossing(g, nextG)) {
      hits.push(bisectCrossing(planet, target, tMs, nextMs, g, adapter, zodiac, ayanamsaId));
    } else if (nextG === 0) {
      hits.push(makePass(planet, target, nextMs, adapter, zodiac, ayanamsaId));
    }
    tMs = nextMs;
    g = nextG;
    if (nextMs >= endMs) break;
  }
  return hits;
}

/**
 * The first exact crossing strictly after `fromUtc`, or null if none happens
 * inside `maxDays`. This is the raw primitive: it does not care whether the
 * crossing is a genuine new cycle or a retrograde re-touch of the same degree
 * — use `computeReturn` for cycle semantics.
 */
export function firstCrossingAfter(
  planet: Planet,
  targetLon: number,
  fromUtc: Date,
  adapter: EphemerisAdapter,
  opts: CrossingSearchOptions & { maxDays?: number } = {},
): ReturnPass | null {
  const zodiac = opts.zodiac ?? 'tropical';
  const ayanamsaId = opts.ayanamsaId ?? 'lahiri';
  const stepMs = (opts.stepDays ?? SCAN_STEP_DAYS[planet]) * MS_PER_DAY;
  const maxMs = (opts.maxDays ?? MEAN_RETURN_DAYS[planet] * 1.6 + 60) * MS_PER_DAY;
  const target = norm360(targetLon);

  // Start one millisecond in so a body already sitting on the target at
  // `fromUtc` (the natal moment) does not report that instant back.
  const startMs = fromUtc.getTime() + 1;
  const endMs = startMs + maxMs;
  let tMs = startMs;
  let g = offsetAt(planet, target, tMs, adapter, zodiac, ayanamsaId);
  while (tMs < endMs) {
    const nextMs = Math.min(tMs + stepMs, endMs);
    const nextG = offsetAt(planet, target, nextMs, adapter, zodiac, ayanamsaId);
    if (isCrossing(g, nextG)) {
      return bisectCrossing(planet, target, tMs, nextMs, g, adapter, zodiac, ayanamsaId);
    }
    if (nextG === 0) return makePass(planet, target, nextMs, adapter, zodiac, ayanamsaId);
    tMs = nextMs;
    g = nextG;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Planetary returns (Saturn / Jupiter / Venus / any body)
// ---------------------------------------------------------------------------

export interface ReturnOptions extends CrossingSearchOptions {
  /** 1 = the next return (default), 2 = the one after that, and so on. */
  ordinal?: number;
}

export interface PlanetReturn {
  planet: Planet;
  zodiac: ReturnZodiac;
  /** The natal longitude the body came back to. */
  targetLon: number;
  /** Which cycle this is, counted from `fromUtc`. */
  ordinal: number;
  /** The return itself: the FIRST exact pass of the group. */
  utc: Date;
  utcISO: string;
  /**
   * Every exact pass of this return. One when the body stays direct; three
   * when it retrogrades back over the degree and then crosses again — the
   * reason a Saturn return is lived as a season rather than a day.
   */
  passes: ReturnPass[];
  /** Years between `fromUtc` and the return. Pass the birth moment to get the age. */
  yearsFromStart: number;
  meanPeriodDays: number;
  /** Plain-language note on the cycle, when we have one. */
  note?: string;
}

/**
 * How far the body must travel away from the natal degree before a crossing
 * counts as a new cycle rather than a retrograde re-touch.
 */
function departureZoneDeg(planet: Planet): number {
  return RETRO_LOOP_DEG[planet] * 1.2 + 1;
}

/** Step forward until the body has genuinely left the natal degree behind. */
function leaveReturnZone(
  planet: Planet,
  target: number,
  fromMs: number,
  adapter: EphemerisAdapter,
  zodiac: ReturnZodiac,
  ayanamsaId: AyanamsaId,
): number {
  const zone = departureZoneDeg(planet);
  const stepMs = SCAN_STEP_DAYS[planet] * MS_PER_DAY;
  const capMs = fromMs + MEAN_RETURN_DAYS[planet] * 0.6 * MS_PER_DAY;
  let tMs = fromMs;
  while (tMs < capMs) {
    tMs = Math.min(tMs + stepMs, capMs);
    if (Math.abs(offsetAt(planet, target, tMs, adapter, zodiac, ayanamsaId)) > zone) return tMs;
  }
  return capMs;
}

/**
 * The planetary return — one function for all of them.
 *
 *   computeReturn('saturn',  natalSaturnLon,  birthUtc, adapter) → age ~29.5
 *   computeReturn('jupiter', natalJupiterLon, birthUtc, adapter) → age ~11.9
 *   computeReturn('venus',   natalVenusLon,   birthUtc, adapter) → ~1 year
 *
 * Pass the BIRTH instant as `fromUtc` and `yearsFromStart` is the age the
 * return lands at. Pass any other instant and you get the next one from there.
 *
 * Cycle semantics: if the body is still sitting on the natal degree at
 * `fromUtc`, the search first waits for it to clear its own retrograde loop,
 * so a Saturn that turns retrograde back over its natal degree three months
 * after birth does not get reported as a Saturn return.
 */
export function computeReturn(
  planet: Planet,
  natalLon: number,
  fromUtc: Date,
  adapter: EphemerisAdapter,
  opts: ReturnOptions = {},
): PlanetReturn {
  const zodiac = opts.zodiac ?? 'tropical';
  const ayanamsaId = opts.ayanamsaId ?? 'lahiri';
  const ordinal = Math.max(1, Math.floor(opts.ordinal ?? 1));
  const target = norm360(natalLon);
  const period = MEAN_RETURN_DAYS[planet];

  let scanFromMs = fromUtc.getTime();
  if (Math.abs(offsetAt(planet, target, scanFromMs, adapter, zodiac, ayanamsaId)) <= departureZoneDeg(planet)) {
    scanFromMs = leaveReturnZone(planet, target, scanFromMs, adapter, zodiac, ayanamsaId);
  }

  let hit: ReturnPass | null = null;
  for (let k = 0; k < ordinal; k++) {
    hit = firstCrossingAfter(planet, target, new Date(scanFromMs), adapter, {
      zodiac,
      ayanamsaId,
      stepDays: opts.stepDays,
    });
    if (!hit) break;
    // Skip past this group's retrograde re-touches before hunting the next cycle.
    scanFromMs = hit.utc.getTime() + period * 0.6 * MS_PER_DAY;
  }
  if (!hit) {
    throw new Error(
      `No ${planet} return to ${target.toFixed(4)}° found within the search horizon from ${fromUtc.toISOString()}`,
    );
  }

  const passes: ReturnPass[] = [hit];
  if (RETRO_SEASON_DAYS[planet] > 0) {
    // Three times the retrograde season comfortably contains all three passes,
    // and capping at 45% of the period keeps the next cycle out of the window.
    const groupSpanMs =
      Math.min(RETRO_SEASON_DAYS[planet] * 3, period * 0.45) * MS_PER_DAY;
    passes.push(
      ...findLongitudeCrossings(
        planet,
        target,
        new Date(hit.utc.getTime() + 1000),
        new Date(hit.utc.getTime() + groupSpanMs),
        adapter,
        { zodiac, ayanamsaId, stepDays: opts.stepDays },
      ),
    );
  }

  return {
    planet,
    zodiac,
    targetLon: target,
    ordinal,
    utc: hit.utc,
    utcISO: hit.utcISO,
    passes,
    yearsFromStart: (hit.utc.getTime() - fromUtc.getTime()) / (TROPICAL_YEAR_DAYS * MS_PER_DAY),
    meanPeriodDays: period,
    note: RETURN_CYCLE_NOTES[planet],
  };
}

/**
 * The Saturn return — the single most-asked Western timing question.
 * `ordinal: 2` gives the second one, near ages 58-60.
 */
export function saturnReturn(
  natalSaturnLon: number,
  birthUtc: Date,
  adapter: EphemerisAdapter,
  opts: ReturnOptions = {},
): PlanetReturn {
  return computeReturn('saturn', natalSaturnLon, birthUtc, adapter, opts);
}

/** The Jupiter return — roughly every 11.9 years. */
export function jupiterReturn(
  natalJupiterLon: number,
  birthUtc: Date,
  adapter: EphemerisAdapter,
  opts: ReturnOptions = {},
): PlanetReturn {
  return computeReturn('jupiter', natalJupiterLon, birthUtc, adapter, opts);
}

/**
 * The Venus return. Geocentrically Venus is back on a natal degree about once
 * a year; `ordinal: 8` gives the eighth, which lands within a couple of days
 * of the natal date — the 8-year Venus pentagram people mean when they say
 * "Venus return".
 */
export function venusReturn(
  natalVenusLon: number,
  fromUtc: Date,
  adapter: EphemerisAdapter,
  opts: ReturnOptions = {},
): PlanetReturn {
  return computeReturn('venus', natalVenusLon, fromUtc, adapter, opts);
}

/** The lunar return — the monthly one, ~27.3 days. */
export function lunarReturn(
  natalMoonLon: number,
  fromUtc: Date,
  adapter: EphemerisAdapter,
  opts: ReturnOptions = {},
): PlanetReturn {
  return computeReturn('moon', natalMoonLon, fromUtc, adapter, opts);
}

// ---------------------------------------------------------------------------
// Return charts
// ---------------------------------------------------------------------------

export interface ReturnChartOptions {
  houseSystem?: HouseSystem;
  ayanamsaId?: AyanamsaId;
}

/** A full chart cast for a return instant at a specific place on Earth. */
export interface ReturnChart {
  utc: Date;
  utcISO: string;
  /** The location the chart was cast for — change it and the houses change. */
  place: { latitude: number; longitude: number };
  houseSystem: HouseSystem;
  ayanamsa: { id: AyanamsaId; valueDeg: number };
  positions: PlanetPosition[];
  angles: AnglesResult;
  houses: HousesResult;
  /** House 1-12 for each Western body, from the cusps above. */
  housePlacements: Partial<Record<Planet, number>>;
  aspects: AspectHit[];
}

/**
 * Cast the chart for an instant at a place. The instant of a return is the
 * same worldwide; everything angular in here is not.
 */
export function castReturnChart(
  utc: Date,
  latitude: number,
  longitudeEast: number,
  adapter: EphemerisAdapter,
  opts: ReturnChartOptions = {},
): ReturnChart {
  const houseSystem = opts.houseSystem ?? 'placidus';
  const ayanamsaId = opts.ayanamsaId ?? 'lahiri';

  const positions = computePositions(utc, adapter, ayanamsaId);
  const ayanDeg = ayanamsaValue(utc, adapter, ayanamsaId);
  const angles = computeAngles(utc, latitude, longitudeEast, adapter, ayanDeg);
  const houses = housesFor(houseSystem, utc, latitude, longitudeEast, adapter, angles);

  const housePlacements: Partial<Record<Planet, number>> = {};
  for (const p of positions) {
    if (WESTERN_PLANETS.includes(p.planet)) {
      housePlacements[p.planet] = houseOf(p.lonTropical, houses.cusps);
    }
  }

  const aspectPoints: ChartPoint[] = [
    ...positions
      .filter((p) => WESTERN_PLANETS.includes(p.planet))
      .map((p) => ({ id: p.planet as ChartPoint['id'], lon: p.lonTropical })),
    { id: 'ascendant', lon: angles.ascendant },
    { id: 'mc', lon: angles.mc },
  ];

  return {
    utc,
    utcISO: utc.toISOString(),
    place: { latitude, longitude: longitudeEast },
    houseSystem,
    ayanamsa: { id: ayanamsaId, valueDeg: Number(ayanDeg.toFixed(6)) },
    positions,
    angles,
    houses,
    housePlacements,
    aspects: computeAspects(aspectPoints),
  };
}

// ---------------------------------------------------------------------------
// Solar return
// ---------------------------------------------------------------------------

export interface SolarReturnResult {
  /** Calendar year (UTC) the return falls in. */
  year: number;
  /** The natal TROPICAL Sun longitude the return was solved against. */
  natalSunLon: number;
  /** The exact instant of the return, solved to under a second. */
  instant: Date;
  instantISO: string;
  /** |returned Sun − natal Sun| in degrees. Effectively zero. */
  residualDeg: number;
  chart: ReturnChart;
  /** The Sun's house in the return chart — the headline of a solar return. */
  sunHouse: number;
  /** The return Ascendant — location-dependent, and the reason you can relocate. */
  ascendant: SignPosition;
  note: string;
}

const SOLAR_RETURN_NOTE =
  'A solar return chart is cast for the moment the Sun comes back to its natal degree, at the place you are for that moment. It is read as a lens on the year, not a forecast of events.';

function buildSolarReturn(
  natalSunLon: number,
  pass: ReturnPass,
  latitude: number,
  longitudeEast: number,
  adapter: EphemerisAdapter,
  opts: ReturnChartOptions,
): SolarReturnResult {
  const chart = castReturnChart(pass.utc, latitude, longitudeEast, adapter, opts);
  return {
    year: pass.utc.getUTCFullYear(),
    natalSunLon: norm360(natalSunLon),
    instant: pass.utc,
    instantISO: pass.utcISO,
    residualDeg: pass.residualDeg,
    chart,
    sunHouse: chart.housePlacements.sun ?? 1,
    ascendant: signPosition(chart.angles.ascendant),
    note: SOLAR_RETURN_NOTE,
  };
}

/**
 * The solar return for a calendar year, cast for a location.
 *
 * The instant is the moment the Sun's tropical longitude equals
 * `natalSunLon`; the Sun passes any given degree once a year, so the first
 * crossing on or after 1 January of `year` (UTC) is that year's return.
 *
 * `latitude`/`longitudeEast` are where the person will BE for the return, not
 * necessarily where they were born — relocating changes the houses, which is
 * exactly what the technique is for.
 */
export function computeSolarReturn(
  natalSunLon: number,
  year: number,
  latitude: number,
  longitudeEast: number,
  adapter: EphemerisAdapter,
  opts: ReturnChartOptions = {},
): SolarReturnResult {
  const yearStart = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
  // Step back a millisecond so a return falling exactly at 00:00:00.000 on
  // 1 January still belongs to this year.
  const pass = firstCrossingAfter(
    'sun',
    natalSunLon,
    new Date(yearStart.getTime() - 1),
    adapter,
    { maxDays: 370 },
  );
  if (!pass) {
    throw new Error(`No solar return to ${norm360(natalSunLon).toFixed(4)}° found in ${year}`);
  }
  return buildSolarReturn(natalSunLon, pass, latitude, longitudeEast, adapter, opts);
}

/**
 * The next solar return at or after `fromUtc`. Pass the birth instant and you
 * get the first birthday return; pass "now" and you get the upcoming one.
 */
export function nextSolarReturn(
  natalSunLon: number,
  fromUtc: Date,
  latitude: number,
  longitudeEast: number,
  adapter: EphemerisAdapter,
  opts: ReturnChartOptions = {},
): SolarReturnResult {
  const pass = firstCrossingAfter('sun', natalSunLon, fromUtc, adapter, { maxDays: 370 });
  if (!pass) {
    throw new Error(
      `No solar return to ${norm360(natalSunLon).toFixed(4)}° found after ${fromUtc.toISOString()}`,
    );
  }
  return buildSolarReturn(natalSunLon, pass, latitude, longitudeEast, adapter, opts);
}
